import React, { Suspense, lazy, memo, useEffect, useState } from 'react';
import { NavLink, Route, Routes } from 'react-router';
import { FormattedMessage, IntlProvider } from 'react-intl';
import ApplicationHeader from '@rio-cloud/rio-uikit/ApplicationHeader';
import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';

import { DefaultUserMenu, EulaConsent } from '@rio-cloud/rio-user-menu-component';
import { SessionExpiredDialog } from '@rio-cloud/rio-session-expired-info';

import { DEFAULT_LOCALE } from '../../configuration/lang/lang';
import { getLanguageData, getLocale } from '../../configuration/lang/langSlice';
import { DriversContainer } from './drivers/main/containers/DriversContainer';
import IframeResizer from '@rio-cloud/iframe-resizer';

import { reportErrorToSentry } from '../../configuration/setup/sentry';
import { config } from '../../config';
import { getTenant, isUserSessionExpired } from '../../configuration/login/loginSlice';
import DefaultRedirect from './router/DefaultRedirect';
import { RioNotifications } from '@rio-cloud/rio-notifications-component';
import { getSessionWarningAcknowledgedRtk, hideSessionWarning } from './appSlice';
import { getIdToken } from '../../configuration/tokenHandling/tokenHandlingSlice';
import { useAppDispatch, useAppSelector } from '../../configuration/setup/hooks.ts';
import { OrderChat } from '@rio-cloud/rio-order-chat-component';
import {
    hasAnalyticsAnalysisOverlayParam,
    removeAnalyticsAnalysisOverlayParam,
} from './utils/analyticsAnalysisOverlayUtils';

interface DriverAdminProps {
    hideSessionWarning: () => void;
    locale: string;
    sessionWarningAcknowledged: boolean;
    userSessionExpired: boolean;
    idToken: string | null;
}

const AnalyticsAnalysisOverlay = lazy(() =>
    import('@rio-cloud/rio-uikit/AnalyticsAnalysisOverlay').then(module => ({
        default: module.AnalyticsAnalysisOverlay,
    }))
);

const DriverAdmin = memo((props: DriverAdminProps) => {
    const { hideSessionWarning, locale, sessionWarningAcknowledged, userSessionExpired, idToken } = props;
    const [showAnalyticsAnalysisOverlay] = useState(() => {
        if (typeof window === 'undefined') {
            return false;
        }

        return hasAnalyticsAnalysisOverlayParam();
    });

    const postLogoutRedirectUri = config.login.postLogoutRedirectUri;

    const environment = import.meta.env.PROD ? 'production' : 'local';

    const userMenu = (
        <DefaultUserMenu
            environment={environment}
            postLogout={
                idToken && postLogoutRedirectUri
                    ? { redirectUri: postLogoutRedirectUri, idTokenHint: idToken }
                    : undefined
            }
        />
    );
    const tenant = useAppSelector(getTenant) || 'unknown';
    const chat = <OrderChat tenant={tenant} />;
    const notifications = <RioNotifications />;
    const actionBarItems = config.isCapabilityMonitoring ? [] : [chat, notifications, userMenu];
    const menuUrl = config.backend.MENU_SERVICE;
    const menu = config.isCapabilityMonitoring ? undefined : <IframeResizer className='iFrameResizer' src={menuUrl} />;
    const appTitle = <FormattedMessage id={'intl-msg:moduleName'} />;
    const isLocalhost = import.meta.env.DEV;
    const getLocalUrl = (port: number) => `http://${window.location.hostname}:${port}`;
    const resolveLink = (liveUrl: string | undefined, localPort: number) =>
        isLocalhost ? getLocalUrl(localPort) : (liveUrl ?? '');

    const navItems = [
        {
            key: 'assets',
            route: (
                <a href={resolveLink(config.links.ASSET_ADMINISTRATION_WEB, 3010)}>
                    <FormattedMessage id={'intl-msg:tabTitle.assets'} />
                </a>
            ),
        },
        {
            key: 'drivers',
            route: (
                <NavLink to={'/drivers'}>
                    <FormattedMessage id={'intl-msg:tabTitle.drivers'} />
                </NavLink>
            ),
        },
        {
            key: 'tags',
            route: (
                <a href={resolveLink(config.links.TAG_ADMINISTRATION_WEB, 3012)}>
                    <FormattedMessage id={'intl-msg:tabTitle.tags'} />
                </a>
            ),
        },
    ];

    const HOME_ROUTE = (
        // biome-ignore lint/a11y/useAnchorContent: ApplicationHeader renders the home/logo slot as an empty anchor.
        <a href={isLocalhost ? window.location.origin : 'https://home.rio.cloud'} />
    );

    useEffect(() => {
        if (!showAnalyticsAnalysisOverlay) {
            return;
        }

        removeAnalyticsAnalysisOverlayParam();
    }, [showAnalyticsAnalysisOverlay]);

    const header = (
        <ApplicationLayout.Header>
            <ApplicationHeader
                actionBarItems={actionBarItems}
                appNavigator={menu}
                homeRoute={HOME_ROUTE}
                label={appTitle}
                navItems={navItems}
            />
            <EulaConsent environment={environment} />
        </ApplicationLayout.Header>
    );

    return (
        <React.Fragment>
            <SessionExpiredDialog
                locale={locale}
                onClose={hideSessionWarning}
                show={userSessionExpired && !sessionWarningAcknowledged}
            />
            <Routes>
                <Route path={'/drivers'} element={<DriversContainer header={header} />} />
                <Route path={'/drivers/:driverId'} element={<DriversContainer header={header} />} />
                <Route path='*' element={<DefaultRedirect />} />
            </Routes>
            {showAnalyticsAnalysisOverlay && (
                <Suspense fallback={null}>
                    <AnalyticsAnalysisOverlay
                        bannerStartCoordinates={{ top: Math.max(0, window.innerHeight - 85), left: 15 }}
                    />
                </Suspense>
            )}
        </React.Fragment>
    );
});

// biome-ignore lint/suspicious/noExplicitAny: should be OnErrorFn instead of (intlError: any) => void, but type does not really matter for us here
const intlErrorHandler: (intlError: any) => void = intlError => {
    reportErrorToSentry(intlError);
    console.error(intlError);
};

export const App = () => {
    const dispatch = useAppDispatch();
    const languageData = useAppSelector(getLanguageData);
    const locale = useAppSelector(getLocale);
    const sessionWarningAcknowledged = useAppSelector(getSessionWarningAcknowledgedRtk);
    const userSessionExpired = useAppSelector(isUserSessionExpired);
    const idToken = useAppSelector(getIdToken);

    if (!languageData) {
        return null;
    }

    return (
        <IntlProvider
            defaultLocale={DEFAULT_LOCALE}
            locale={locale ?? DEFAULT_LOCALE}
            messages={languageData}
            onError={intlErrorHandler}
        >
            <DriverAdmin
                hideSessionWarning={() => {
                    dispatch(hideSessionWarning());
                }}
                locale={locale ?? DEFAULT_LOCALE}
                sessionWarningAcknowledged={sessionWarningAcknowledged}
                userSessionExpired={userSessionExpired}
                idToken={idToken}
            />
        </IntlProvider>
    );
};

export default App;
